import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import Monogram from "@/components/Monogram";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/real-estate-activities", label: "Real Estate Activities" },
  { to: "/partners-approach", label: "Partners & Approach" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 w-full transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-border shadow-[0_1px_0_0_rgba(0,0,0,0.02)]"
          : "bg-white border-b border-transparent"
      }`}
    >
      <div className="container-tight flex h-20 items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="brand-link"
        >
          <Monogram className="h-9 w-9 transition-transform group-hover:scale-[1.03]" />
          <div className="leading-tight">
            <div className="font-display text-[17px] font-bold tracking-tight text-navy">
              Taonga OÜ
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-charcoal/60">
              Estonia
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
          {NAV.slice(0, 5).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `nav-link text-[14px] font-medium tracking-wide transition-colors ${
                  isActive ? "active text-navy" : "text-charcoal hover:text-navy"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Button
            asChild
            data-testid="header-contact-cta"
            className="bg-navy hover:bg-navy/90 text-white rounded-md px-5"
          >
            <Link to="/contact">Contact</Link>
          </Button>
        </div>

        {/* Mobile */}
        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-border text-charcoal hover:text-navy hover:border-warm transition-colors"
                aria-label="Open menu"
                data-testid="mobile-menu-trigger"
              >
                <Menu className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white p-0 w-[88%] sm:w-[420px]">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <SheetDescription className="sr-only">
                Site navigation menu for Taonga OÜ
              </SheetDescription>
              <div className="flex flex-col h-full">
                <div className="flex items-center gap-3 px-6 h-20 border-b border-border">
                  <Monogram className="h-9 w-9" />
                  <div className="font-display text-[17px] font-bold text-navy">
                    Taonga OÜ
                  </div>
                </div>
                <nav className="flex-1 px-6 py-8 space-y-1" aria-label="Mobile">
                  {NAV.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/"}
                      data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={({ isActive }) =>
                        `block py-3 text-[15px] font-medium border-b border-border/60 ${
                          isActive ? "text-navy" : "text-charcoal hover:text-navy"
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </nav>
                <div className="px-6 pb-8">
                  <Button
                    asChild
                    className="w-full bg-navy hover:bg-navy/90 text-white"
                    data-testid="mobile-contact-cta"
                  >
                    <Link to="/contact">Contact</Link>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
